import styled from '@emotion/styled';
import Flex from '@react-css/flex';
import Body from 'storybookConfig/stories/molecules/Body';
import Heading from 'storybookConfig/stories/molecules/Heading';
import Card from 'storybookConfig/stories/cells/Card';
import RevenueSliderStandalone from 'containers/MarketplaceOnboardingPage/RevenueSliderStandalone';
import Divider from 'storybookConfig/stories/molecules/Divider';
import { ThemeColors } from 'storybookConfig/stories/theme';
import IconTile from 'storybookConfig/stories/molecules/IconTile';
import type { IconName } from 'storybookConfig/mixins/icons';

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const Title = styled(Heading)`
  text-align: center;
  margin-bottom: 8px;
`;

const Subtitle = styled(Body)`
  text-align: center;
  color: ${({ theme }) => theme.color.bodyTextSecondary};
  margin-bottom: 24px;
`;

const SplitContainer = styled(Flex)`
  justify-content: center;
  align-items: center;
  gap: 24px;
  margin: 48px 0;
`;

const SplitBox = styled.div<{ color: string }>`
  background: ${({ color }) => color};
  border-radius: 8px;
  color: ${({ theme }) => theme.color.bodyTextOnDark};
  text-align: center;
  width: 130px;
  min-width: 100px;
  min-height: 70px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const SplitPercentage = styled.div`
  font-size: 24px;
  font-weight: 700;
`;

const SplitLabel = styled.div`
  margin-top: 8px;
  font-size: 14px;
  letter-spacing: -0.01em;
  white-space: nowrap;
`;

const Operator = styled.div`
  font-size: 24px;
  color: ${({ theme }) => theme.color.bodyTextSecondary};
`;

const FooterNote = styled(Body)`
  color: ${({ theme }) => theme.color.bodyTextSecondary};
  text-align: center;
  margin-top: 32px;
  font-size: 14px;
`;

const LearnMoreLink = styled.a`
  color: ${({ theme }) => theme.color.accent};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

interface RevenueSplitPanelProps {
  revSplit?: number;
  /** The Lucide icon above the title, in an `IconTile`. */
  iconName: IconName;
  onRevSplitChange?: (value: number) => void;
}

const RevenueSplitPanel = ({
  revSplit = 60,
  iconName,
  onRevSplitChange,
}: RevenueSplitPanelProps) => {
  const retailerSplit = 100 - revSplit;
  return (
    <Card>
      <div style={{ textAlign: 'center', padding: '0 80px' }}>
        <IconContainer>
          <IconTile name={iconName} />
        </IconContainer>
        <Title variant="Headings/H3">Revenue Split</Title>
        <Subtitle>
          This is the split that the retailer and you, the supplier, will share. You can set the
          amount your brand wants using the slider below or the input field on the right.
        </Subtitle>
        <Divider margin="24px 0 48px" />

        {onRevSplitChange && (
          <div style={{ margin: '24px 0', width: '100%' }}>
            <RevenueSliderStandalone
              value={revSplit}
              onChange={onRevSplitChange}
              min={0}
              max={100}
              step={1}
              width="100%"
            />
          </div>
        )}
        <SplitContainer>
          <div>
            <SplitBox color={ThemeColors.successBase}>
              <SplitPercentage>{retailerSplit}.0%</SplitPercentage>
            </SplitBox>
            <SplitLabel>Retailer</SplitLabel>
          </div>
          <Operator>+</Operator>
          <div>
            <SplitBox color={ThemeColors.accent}>
              <SplitPercentage>{revSplit}.0%</SplitPercentage>
            </SplitBox>
            <SplitLabel>You Keep</SplitLabel>
          </div>
          <Operator>=</Operator>
          <div>
            <SplitBox
              color={`var(--Gradients-Linear-225-Brand, linear-gradient(225deg, ${ThemeColors.accent} 14.64%, ${ThemeColors.successBase} 85.36%))`}
            >
              <SplitPercentage>100%</SplitPercentage>
            </SplitBox>
            <SplitLabel>Total</SplitLabel>
          </div>
        </SplitContainer>
        <FooterNote>
          *The retailer pays a per transaction fee of 5% or higher.{' '}
          <LearnMoreLink href="#">Learn more</LearnMoreLink>
        </FooterNote>
      </div>
    </Card>
  );
};

export default RevenueSplitPanel;
